import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import BoxWithIcon from "../atoms/ComponentBox/BoxWithIcon";
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectPermissions } from '../redux/permissionsSlice';

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

const StyledTabs = styled(Tabs)({
  backgroundColor: '#e1e7ef',
  borderRadius: '10px',
  padding: '4px',
  '& .MuiTabs-indicator': {
    display: 'none',
  },
});

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '14px',
  minHeight: '36px',
  padding: '8px 16px',
  borderRadius: '5px',
  color: theme.palette.text.secondary,
  '&.Mui-selected': {
    color: '#fff',
    backgroundColor: '#635bff',
  },
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  borderBottom: '1px solid #e1e7ef',
  padding: '10px 15px',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#5BB4D2'
  },
}));

const Accounts = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const permissions = useSelector(selectPermissions);
  const [activeTab, setActiveTab] = useState(0);

  const hasModulePermission = (moduleName) => {
    const modulePermission = permissions.find(
      permission => permission.ModuleName === moduleName
    );
    
    if (!modulePermission) return false;

    return (
      modulePermission.AddRec === "1" ||
      modulePermission.EditRec === "1" ||
      modulePermission.DeleteRec === "1" ||
      modulePermission.ViewRec === "1"
    );
  };

  const tabs = [
    { 
      label: "Accounts", 
      path: "/accounts", 
      moduleName: "Accounts",
    }
  ];


  const availableTabs = tabs.filter(tab => hasModulePermission(tab.moduleName));

  useEffect(() => {
    if (location.pathname === '/accounts' && availableTabs.length > 0) {
      setActiveTab(0);
      return;
    }

    const currentTabIndex = availableTabs.findIndex((tab) => tab.path === location.pathname);
    if (currentTabIndex !== -1) {
      setActiveTab(currentTabIndex);
    }
  }, [location.pathname, availableTabs.length]);

  const handleChange = (event, newValue) => {
    setActiveTab(newValue);
    navigate(availableTabs[newValue].path, { state: { permissions } });
  };

  if (availableTabs.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">
          No accounts sections are available based on your permissions.
        </Typography>
      </Box>
    );
  }


  const accountsCards = [
    {
      component: (
        <BoxWithIcon
          key="payment-advice"
          totalCount={10}
          activeCount={10}
          inactiveCount={0}
          link={{ pathname: '/advicegeneratetable' }}
          permissions={permissions}
          title="Payment Advice"
        />
      ),
      moduleName: "Payment Advice",
      show: hasModulePermission("Payment Advice")
    },
    {
      component: (
        <BoxWithIcon
          key="credit-notes"
          totalCount={10}
          activeCount={10}
          inactiveCount={0}
          link={{ pathname: '/cradit-notestable' }}
          permissions={permissions}
          title="Credit Notes"
        />
      ),
      moduleName: "Credit Notes",
      show: hasModulePermission ("Credit Notes")
    },
    {
      component: (
        <BoxWithIcon
          key="receipt-voucher"
          totalCount={10}
          activeCount={10}
          inactiveCount={0}
          link={{ pathname: '/receipt-table' }}
          permissions={permissions}
          title="Receipt Voucher"
        />
      ),
      moduleName: "Receipt Voucher",
      show: hasModulePermission("Receipt Voucher")
    },
    {
      component: (
        <BoxWithIcon
          key="refund"
          totalCount={10}
          activeCount={10}
          inactiveCount={0}
          link={{ pathname: '/refundtable' }}
          // permissions={permissions}
          title="Refund"
        />
      ),
      moduleName: "Refund",
      show: ("Refund")
    },
    {
      component: (
        <BoxWithIcon
          key="transaction-topup"
          totalCount={10}
          activeCount={10}
          inactiveCount={0}
          link={{ pathname: '/trntopuptable' }}
          permissions={permissions}
          title="Transaction TopUp"
        />
      ),
      moduleName: "Transaction TopUp",
      show: hasModulePermission("Transaction TopUp")
    }
  ];


  const visibleCards = accountsCards.filter(card => card.show);

  return (
    <Box sx={{ 
      width: '100%', 
      p: 2, 
      '& .MuiTabs-root': {
        padding: '7px 4px 0px 4px !important'
      } 
    }}>
      <Box sx={{ mb: 4 }}>
        <StyledTabs 
          value={activeTab} 
          onChange={handleChange} 
          sx={{ display: { xs: 'block', sm: 'none', md: 'block', lg: 'block' } }}
        >
          {availableTabs.map((tab, index) => (
            <StyledTab key={index} label={tab.label} />
          ))}
        </StyledTabs>

        <Box sx={{ display: { xs: 'block', sm: 'none' }, mt: 2 }}>
          <List>
            {availableTabs.map((tab, index) => (
              <StyledListItem 
                button 
                key={index} 
                onClick={() => {
                  setActiveTab(index);
                  navigate(tab.path, { state: { permissions } });
                }}
              >
                <ListItemText primary={tab.label} />
              </StyledListItem>
            ))}
          </List>
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1 }}>
        <Grid container sx={{
          width: "100%",
          maxWidth: "100%",
          marginTop: '10px'
        }}>
          <Grid item xs={12}>
            <Grid container spacing={1}>
              {visibleCards.length > 0 ? (
                visibleCards.map((card, index) => (
                  <React.Fragment key={index}>
                    {card.component}
                  </React.Fragment>
                ))
              ) : (
                <Grid item xs={12}>
                  <Typography variant="body1" align="center" sx={{ p: 3 }}>
                    No accounts cards are available based on your permissions.
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Accounts;